import type { User } from '@/api/types';

/**
 * Permission keys, mirroring Auth/PermissionCatalog.cs on the server.
 *
 * The server is the enforcement point; these gates only decide what to render.
 * Hiding a button the API would reject is a courtesy, not a security boundary.
 */
export const PERMISSIONS = {
  medicinesView: 'medicines.view',
  medicinesCreate: 'medicines.create',
  medicinesEdit: 'medicines.edit',
  medicinesDelete: 'medicines.delete',
  medicinesAdjustStock: 'medicines.adjust_stock',
  medicinesBulkImport: 'medicines.bulk_import',
  medicinesViewCosts: 'medicines.view_costs',

  salesView: 'sales.view',
  salesCreate: 'sales.create',
  salesEdit: 'sales.edit',
  salesDelete: 'sales.delete',
  salesDiscount: 'sales.discount',
  salesRefund: 'sales.refund',
  salesExport: 'sales.export',

  purchasesView: 'purchases.view',
  purchasesCreate: 'purchases.create',
  purchasesReceive: 'purchases.receive',
  purchasesDelete: 'purchases.delete',
  suppliersView: 'suppliers.view',
  suppliersManage: 'suppliers.manage',

  customersView: 'customers.view',
  customersManage: 'customers.manage',

  reportsSales: 'reports.sales',
  reportsInventory: 'reports.inventory',
  reportsExpiry: 'reports.expiry',
  reportsFinancials: 'reports.financials',

  ocrUse: 'ocr.use',
  exportsCreate: 'exports.create',
  alertsManage: 'alerts.manage',
  settingsView: 'settings.view',
  settingsEdit: 'settings.edit',
  auditView: 'audit.view',
  usersView: 'users.view',
  usersManage: 'users.manage',
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Superadmin bypasses everything. Admin does not — the role is a label, and
 * every user carries an explicit permission map, exactly as on the server.
 */
export function hasPermission(user: User | null | undefined, key: PermissionKey): boolean {
  if (!user) return false;
  if (user.role === 'Superadmin') return true;
  return user.permissions[key] === true;
}

export function hasAny(user: User | null | undefined, keys: PermissionKey[]): boolean {
  return keys.some((key) => hasPermission(user, key));
}

export function hasAll(user: User | null | undefined, keys: PermissionKey[]): boolean {
  return keys.every((key) => hasPermission(user, key));
}
